// src/shared/components/EditableField.jsx
import React, { useState } from 'react';
import PropTypes from 'prop-types';

/**
 * EditableField
 *
 * A field that displays a value with a pencil icon to enter edit mode.
 * Changes are propagated to the parent immediately while typing.
 * The checkmark button closes edit mode (and trims whitespace).
 *
 * Props:
 *  - label: string
 *  - name: string
 *  - value: string|null
 *  - placeholder: string
 *  - onChange: (newValue: string) => void
 *  - type: string – input type, default "text"
 */
export default function EditableField({
  label,
  name,
  value,
  placeholder,
  onChange,
  type = 'text',
}) {
  const [editing, setEditing] = useState(!value);

  const handleChange = (e) => {
    onChange(e.target.value);
  };

  const handleClose = () => {
    setEditing(false);
    // Trim whitespace when closing
    if (value && value !== value.trim()) {
      onChange(value.trim());
    }
  };

  return (
    <div className="mb-3">
      <label htmlFor={name} className="form-label form-label-caps">
        {label}{' '}
        {!value && <i className="bi bi-exclamation-circle-fill text-danger small" />}
      </label>
      <div className="d-flex align-items-center">
        {editing ? (
          <>
            <input
              id={name}
              name={name}
              type={type}
              className="form-control"
              placeholder={placeholder}
              value={value || ''}
              onChange={handleChange}
            />
            <button
              type="button"
              className="btn btn-link btn-sm ms-2"
              onClick={handleClose}
              title="Done editing"
              aria-label={`Done editing ${label}`}
            >
              <i className="bi bi-check-circle-fill text-success"></i>
            </button>
          </>
        ) : (
          <>
            <span className="flex-grow-1">{value || <span className="text-muted">{placeholder}</span>}</span>
            <button
              type="button"
              className="btn btn-link btn-sm"
              onClick={() => setEditing(true)}
            >
              <i className="bi bi-pencil-square"></i>
            </button>
          </>
        )}
      </div>
    </div>
  );
}

EditableField.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  type: PropTypes.string,
};

EditableField.defaultProps = {
  value: '',
  placeholder: '',
  type: 'text',
};
